import Loadable from '@react-loadable/revised'
import React from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

// Attacks
import { LoadableLoading } from 'components/common/LoadableLoading'
import { Dashboard } from 'components/dashboard/Dashboard'
import { HelpPage } from 'components/help/HelpPage'
import { NoTeamLandingPage } from 'components/NoTeamLandingPage'
import { Profile } from 'components/profile/Profile'
import { TrackedGremlinRouteElement } from 'components/TrackedGremlinRouteElement'
import { NO_TEAM } from 'context/AuthContext'
import useAuth from 'hooks/use-auth'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import { prefetchPaginationData } from 'hooks/use-gremlin-query'
import useLongRunningApiData from 'hooks/use-long-running-api-data'
import { serviceQueryKeys } from 'hooks/use-services'
import { hasSupportAppPermission } from 'support/helpers/support-rbac'
import {
  companiesIdGet,
  orgsIdGet,
  schedulesAttacksGet,
  schedulesScenariosGet,
  // schedulesServicesGet,
  testSuitesGet,
  usersGet
} from 'utils/endpoints-access'

const ApiDocs = Loadable({
  loader: () => import(/* webpackChunkName: "ApiDocs" */ 'components/ApiDocs'),
  loading: LoadableLoading
})

const JiraPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "JiraPage" */ 'components/rbac-settings/integrations/jira/JiraPage'
    ),
  loading: LoadableLoading
})

const OAuthPage = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "OAuthPage" */ 'components/rbac-settings/OAuthPage'
    ),
  loading: LoadableLoading
})

const CompanySettings = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "CompanySettings" */ 'components/rbac-settings/company/CompanySettings'
    ),
  loading: LoadableLoading
})

const TeamSettings = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "TeamSettings" */ 'components/rbac-settings/teams/TeamSettings'
    ),
  loading: LoadableLoading
})

// New Create Experiment Routes
const AttacksIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "AttacksIndex" */ 'components/attacks/AttacksIndex'
    ),
  loading: LoadableLoading
})

const SchedulesOldIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SchedulesOldIndex" */ 'components/schedules-old/SchedulesIndex'
    ),
  loading: LoadableLoading
})

const ScenariosIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ScenariosIndex" */ 'components/scenarios/ScenariosIndex'
    ),
  loading: LoadableLoading
})

const SupportAppLegacy = Loadable({
  loader: () =>
    import(/* webpackChunkName: "SupportAppLegacy" */ 'support/index'),
  loading: LoadableLoading
})

const SupportApp = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SupportApp" */ 'components/support/SupportIndex'
    ),
  loading: LoadableLoading
})

const ClientsIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ClientsIndex" */ 'components/clients/ClientsIndex'
    ),
  loading: LoadableLoading
})

const ServicesIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ServicesIndex" */ 'components/services/ServicesIndex'
    ),
  loading: LoadableLoading
})

const GameDaysIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "GameDaysIndex" */ 'components/gamedays/GameDaysIndex'
    ),
  loading: LoadableLoading
})

const SettingsIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SettingsIndex" */ 'components/rbac-settings/SettingsIndex'
    ),
  loading: LoadableLoading
})

const NotFoundPage = Loadable({
  loader: () =>
    import(/* webpackChunkName: "NotFoundPage" */ 'components/NotFoundPage'),
  loading: LoadableLoading
})

const TestSuitesIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "TestSuitesIndex" */ 'components/test-suites/TestSuitesIndex'
    ),
  loading: LoadableLoading
})

const StatusChecksIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "StatusChecksIndex" */ 'components/status-checks/StatusChecksIndex'
    ),
  loading: LoadableLoading
})

const FailureFlagsIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "FailureFlagsIndex" */ 'components/failure-flags/FailureFlagsIndex'
    ),
  loading: LoadableLoading
})

const ReportsIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "ReportsIndex" */ 'components/reliability-management/ReportsIndex'
    ),
  loading: LoadableLoading
})

const GettingStartedIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "GettingStartedIndex" */ 'components/getting-started/GettingStartedIndex'
    ),
  loading: LoadableLoading
})

const NowRunningIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "NowRunningIndex" */ 'components/now-running/NowRunningIndex'
    ),
  loading: LoadableLoading
})

const SchedulesIndex = Loadable({
  loader: () =>
    import(
      /* webpackChunkName: "SchedulesIndex" */ 'components/schedules/SchedulesIndex'
    ),
  loading: LoadableLoading
})

/**
 * This is a temporary component to iteratively migrate the AuthenticatedRoutes component to react-router v6
 */
export const AuthenticatedRoutes: React.FC = () => {
  const { nowRunning: isNowRunningEnabled } = useFeatureFlags()

  const {
    currentTeamId,
    hasEndpointAccess,
    hasFailureFlags,
    hasFaultInjection,
    hasReliabilityManagement,
    session
  } = useAuth()

  const userHasTeams = currentTeamId !== NO_TEAM

  const userCanViewCompanySettings = hasEndpointAccess(companiesIdGet)

  const userCanViewTeamSettings =
    hasEndpointAccess(orgsIdGet) && hasEndpointAccess(usersGet)

  const hasViewTestSuitesPrivilege = hasEndpointAccess(testSuitesGet)

  const hasSchedulesPrivilege =
    // add this check when available in swagger openapi
    // hasEndpointAccess(schedulesServicesGet, {
    //   teamId: currentTeamId
    // }) ||
    hasEndpointAccess(schedulesScenariosGet, {
      teamId: currentTeamId
    }) ||
    hasEndpointAccess(schedulesAttacksGet, {
      teamId: currentTeamId
    })

  const shouldShowNewSchedules = isNowRunningEnabled && hasSchedulesPrivilege

  useLongRunningApiData(userHasTeams)

  React.useEffect(() => {
    // Prefetch services because the api call is slow
    // Cannot use usePrefetchPaginationData due to feature flag condition
    if (userHasTeams && hasReliabilityManagement) {
      prefetchPaginationData(
        `/services/?pageSize=20&teamId=${currentTeamId}`,
        {},
        { queryKey: serviceQueryKeys.allPaged }
      )
    }
  }, [currentTeamId, hasReliabilityManagement, userHasTeams])

  return (
    <Routes>
      {userHasTeams && (
        <>
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<Navigate to="/dashboard" />}
                name="Onboarding"
              />
            }
            path="/onboarding"
          />
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<Dashboard />}
                name="Dashboard (FI)"
              />
            }
            path="/dashboard"
          />
          {isNowRunningEnabled && (
            <Route element={<NowRunningIndex />} path="/now-running/*" />
          )}
          {shouldShowNewSchedules && (
            <Route element={<SchedulesIndex />} path="/new-schedules/*" />
          )}
          <Route
            element={
              hasViewTestSuitesPrivilege ? (
                <TestSuitesIndex />
              ) : (
                <Navigate to="/" />
              )
            }
            path="/test-suites/*"
          />
          <Route element={<ClientsIndex />} path="/clients/*" />
          <Route element={<SettingsIndex />} path="/settings/*" />
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<Profile />}
                name="User Profile"
              />
            }
            path="/profile/:tab?"
          />
          <Route element={<StatusChecksIndex />} path="/health-checks/*" />
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<JiraPage />}
                name="Jira"
              />
            }
            path="/jira"
          />
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<HelpPage />}
                name="Help"
              />
            }
            path="/help"
          />
          {/*
            This serves as a callback for the oauth integration flow
            After the user completes the flow with the OAuth provider and our
            API, the user will be redirected from the API back to the webapp
            at this path. The URL will contain a query parameter called `msg`
            which contains either a success or error message to display to the user
          */}
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<OAuthPage />}
                name="OAuth"
              />
            }
            path="/oauth-integration"
          />

          <Route
            element={
              hasSupportAppPermission(session) ? (
                <SupportAppLegacy />
              ) : (
                <Navigate to="/" />
              )
            }
            path="/support/*"
          />
          <Route
            element={
              hasSupportAppPermission(session) ? (
                <SupportApp />
              ) : (
                <Navigate to="/" />
              )
            }
            path="/supportnew/*"
          />

          {hasFaultInjection && (
            <>
              <Route element={<AttacksIndex />} path="/attacks/*" />
              <Route element={<SchedulesOldIndex />} path="/schedules/*" />
              <Route element={<ScenariosIndex />} path="/scenarios/*" />
              <Route element={<GameDaysIndex />} path="/gamedays/*" />
              <Route
                element={
                  <TrackedGremlinRouteElement
                    loggedIn
                    component={<ApiDocs />}
                    name="API Docs"
                  />
                }
                path="/api"
              />
            </>
          )}

          {hasFailureFlags && (
            <Route element={<FailureFlagsIndex />} path="/failure-flags/*" />
          )}

          {hasReliabilityManagement && (
            <>
              <Route
                element={<GettingStartedIndex />}
                path="/getting-started/*"
              />
              <Route element={<Navigate to="/getting-started" />} path="/" />
              <Route element={<ReportsIndex />} path="/reports/*" />
              <Route element={<ServicesIndex />} path="/services/*" />
            </>
          )}

          {!hasReliabilityManagement && (
            <Route element={<Navigate to="/dashboard" />} path="/" />
          )}

          {/* these make it so the user doesn't get a 404 if they hit the back button after logging in */}
          <Route element={<Navigate to="/" />} path="/login/*" />
          <Route element={<Navigate to="/" />} path="/users/sso/*" />
          <Route element={<NotFoundPage />} path="*" />
        </>
      )}

      {!userHasTeams && (
        <>
          <Route element={<NoTeamLandingPage />} path="/noteam" />
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<Profile />}
                name="User Profile"
              />
            }
            path="/profile/:tab?"
          />
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<TeamSettings />}
                name="Team Settings"
                shouldRenderRoute={userCanViewTeamSettings}
              />
            }
            path="/settings/team/:teamId/:tab?"
          />
          <Route
            element={
              <TrackedGremlinRouteElement
                loggedIn
                component={<CompanySettings />}
                name="Company Settings"
                shouldRenderRoute={userCanViewCompanySettings}
              />
            }
            path="/settings/:tab?"
          />

          {/* everything else redirects to /noteam */}
          <Route element={<Navigate to="/noteam" />} path="*" />
        </>
      )}
    </Routes>
  )
}
